import React from "react";
import {autorun} from "mobx";

import {PopupConfiguration} from "../../../config/popup-configuration";
import {observer} from "mobx-react";
import {Accordion, AccordionTitleProps, Pagination, PaginationProps} from "semantic-ui-react";

import {PopupPanelItem} from "./popup-panel-item/popup-panel-item";
import {Icon} from "../../icon/icon";
import {IAppStore, withStore} from "../../../stores/abstract-app-store";
import FieldInfoFormat from "@arcgis/core/popup/support/FieldInfoFormat";

interface PopupPanelProps {
    config: PopupConfiguration;
    title: string;
    index: number;
    store?: IAppStore; // this is injected by withStore
    onClick?: (e: React.MouseEvent, titleProps: AccordionTitleProps) => void;
    onHighlightClick: (e: React.MouseEvent, panelIndex: number, index: number) => void;
    onZoomInClick: (e: React.MouseEvent, panelIndex: number, index: number) => void;
    onHighlight: (panelIndex: number, index: number, store: IAppStore) => void;
}
@observer
class PopupPanelBase extends React.Component<PopupPanelProps, any> {
    index: number;
    nullableFieldType = ["small-integer", "integer", "single", "double", "long", "string"];
    numericFieldType = ["small-integer", "integer", "single", "double", "long"];
    peparedFields: string[];

    constructor(props: PopupPanelProps) {
        super(props);
        this.state = {
            items: [],
            title: props.title.substring(0,9)==="[feature]"? props.title.substring(9): props.title
        };
        this.index = props.index;
        this.handlePaginationChange = this.handlePaginationChange.bind(this);
        this.prepareDefaultFormat = this.prepareDefaultFormat.bind(this);
        this.setTitle = this.setTitle.bind(this);
        this.peparedFields=[];
    }

    componentDidMount(): void {
        const {
            index,
            store
        } = this.props;
        // Feldtypen welche im NULL Fall mit "-" ersetzt werden
        autorun((reaction: any) => {
            if (!store?.mapStore.view) {
                return;
            }
            const popupStore = store?.PopupStore;
            if (!popupStore) {
                return [];
            }
            const activePages: number[] = popupStore.activePages;
            activePages[index] = 1;
            popupStore.setActivePages(activePages);
            reaction.dispose();
        });
    }
    render(): React.ReactElement | null {
        const { title } = this.state;
        const {index, onClick, store} = this.props;
        if (!store?.PopupStore) {
            return null;
        }
        const items = this.prepareItems();
        const popupStore = store?.PopupStore;

        let contentClass = "geo-panel";
        if (popupStore?.selectedPanelIndex === index) {
            contentClass+= " geo-panel-selected";
        }
        return (
            <div><Accordion.Title
                index={index}
                active={popupStore?.activeIndex.includes(index)}
                onClick={(e: React.MouseEvent, titleProps: AccordionTitleProps) => {
                    if (onClick) {
                        onClick(e, titleProps);
                    }
                }}
            >
                <Icon name="navigate_plus"/>
                <Icon name="navigate_minus"/>
                <span dangerouslySetInnerHTML={{__html: title}} />
            </Accordion.Title>
            <Accordion.Content className={contentClass} active={popupStore?.activeIndex.includes(index)}
                data-index={index}>
                {items.length > 1 &&
                    <Pagination
                        defaultActivePage={1}
                        firstItem={null}
                        lastItem={null}
                        size="small"
                        pointing
                        secondary
                        totalPages={items.length}
                        onPageChange={this.handlePaginationChange}
                    />
                }
                {items}
            </Accordion.Content></div>
        );
    }

    private prepareItems(){
        const {
            config,
            index,
            store,
            onHighlightClick,
            onZoomInClick
        } = this.props;
        if (!store?.mapStore.view) {
            return [];
        }
        const popupStore = store?.PopupStore;
        if (!popupStore) {
            return [];
        }
        const items: any[] = [];
        let featureIndex = 1;

        popupStore.panels[index - 1].graphics.forEach((graphic, i) => {
            // sourceLayer on Graphic is not documented!
            const sourceLayer = (graphic as any).sourceLayer;
            this.prepareDefaultFormat(sourceLayer);
            const valueUnitMap = popupStore.valueUnitMap.get(sourceLayer.uid as string);
            const fieldMap = popupStore.defaultFormatMap.get(sourceLayer.uid as string);
            if (!fieldMap) {
                return;
            }
            fieldMap.forEach((fieldFormat: FieldInfoFormat, key: string )=> {
                this.prepareAttribute(sourceLayer, fieldFormat, key, valueUnitMap, graphic, popupStore);
            });
            items.push(
                <PopupPanelItem
                    config={config}
                    graphic={graphic}
                    featureIndex={featureIndex}
                    panelIndex={index}
                    key={(graphic.getObjectId() ? graphic.getObjectId(): (graphic as any).uid)
                        + "-" + sourceLayer.uid}
                    titleHandle={this.setTitle}
                    onHighlightClick={onHighlightClick}
                    onZoomInClick={onZoomInClick} />
            );
            featureIndex++;
        });
        return items;
    }
    private prepareAttribute(sourceLayer, fieldFormat: FieldInfoFormat, k: string, valueUnitMap,
                             graphic, popupStore): void {
        if(popupStore.peparedFields.includes(sourceLayer.uid+graphic.getObjectId()+k)){
            return;
        }
        popupStore.peparedFields.push(sourceLayer.uid+graphic.getObjectId()+k);
        if (graphic.attributes.hasOwnProperty(k)) {
            // Alle NULL Werte mit "-" überschreiben
            if (graphic.attributes[k] === null) {
                const fieldType = sourceLayer.getField(k)?.type;
                if (this.nullableFieldType.indexOf(fieldType) > -1) {
                    graphic.attributes[k] = "-";
                }
            } else {
                // Numerische Werte selber formatieren
                let fieldType = sourceLayer.getField(k)?.type;
                // ImageryLayer Raster.ServicePixelValue als double behandeln
                if(!fieldType && sourceLayer.type==="imagery"
                    && k.toLowerCase().indexOf("raster.servicepixelvalue")===0){
                    fieldType = "double";
                }
                if(!Number.isNaN(graphic.attributes[k])
                    && this.numericFieldType.indexOf(fieldType) > -1
                    && !sourceLayer.getFieldDomain(k)) {
                    const o: any = {};
                    if(fieldFormat){
                        if("places" in fieldFormat && typeof fieldFormat.places === "number"){
                            o.maximumFractionDigits = fieldFormat.places;
                        }
                        if("digitSeparator" in fieldFormat && typeof fieldFormat.digitSeparator === "boolean"){
                            o.useGrouping  = fieldFormat.digitSeparator;
                        }
                    }
                    graphic.attributes[k] =
                        new Intl.NumberFormat("de-CH", o).format(graphic.attributes[k]);
                }
            }
            // Attribute auf Masseinheit in valueUnitMap überprüfen
            if (valueUnitMap) {
                const unit = valueUnitMap.get(k);
                if (typeof unit === "string" && graphic.attributes[k] !== null) {
                    graphic.attributes[k] += " " + unit;
                }
            }
        }
    }
    private handlePaginationChange(e: React.MouseEvent, pagination: PaginationProps): void {
        const { activePage } = pagination;
        const { index, store, onHighlight } = this.props;
        if (!store) {
            return;
        }
        const popupStore = store.PopupStore;

        const activePageIndex: number = +`${activePage}`;
        popupStore.setActivePage(index, activePageIndex);
        popupStore.setSelectedPanelIndex(index);
        onHighlight(index, activePageIndex, store);
    }

    private setTitle(newTitle: string): void {
        const { title } = this.props;
        if (title.substring(0,9) === "[feature]") {
            this.setState({ title: newTitle });
        }
    }

    private prepareDefaultFormat(layer: any): void {
        const { store } = this.props;
        if(store?.PopupStore.defaultFormatMap.get(layer.uid as string)) {
            return;
        }
        const fieldMap = new Map();
        if(layer.popupTemplate.fieldInfos
            && Array.isArray(layer.popupTemplate.fieldInfos)) {
            layer.popupTemplate.fieldInfos.forEach((fieldInfo: __esri.FieldInfo) => {
                this.setFieldInfoFormat(layer, fieldInfo, fieldMap);
            });
        }
        if (layer.popupTemplate.content && Array.isArray(layer.popupTemplate.content)) {
            layer.popupTemplate.content.forEach((content: __esri.FieldsContent) => {
                if(content.type === "fields" && Array.isArray(content.fieldInfos)) {
                    content.fieldInfos.forEach((fieldInfo: __esri.FieldInfo) => {
                        this.setFieldInfoFormat(layer, fieldInfo, fieldMap);
                    });
                }
            });
        }
        // Das Property uid ist nicht dokumentiert!
        store?.PopupStore.defaultFormatMap.set((layer as any).uid, fieldMap);
    }

    private setFieldInfoFormat(layer: any, fieldInfo: __esri.FieldInfo, fieldMap: Map<any, any>) {
        let fieldType = layer.getField(fieldInfo.fieldName)?.type;
        // ImageryLayer Raster.ServicePixelValue als double behandeln
        if(!fieldType && layer.type==="imagery"
            && fieldInfo.fieldName.toLowerCase().indexOf("raster.servicepixelvalue")===0){
            fieldType = "double";
        }
        // Datumsformat vorgeben falls nicht definiert
        if (!fieldInfo.format && fieldType === "date") {
            fieldInfo.format = new FieldInfoFormat({dateFormat: "short-date"});
        }
        fieldMap.set(fieldInfo.fieldName, fieldInfo.format?.clone());
        // Bei numerischen Werte Formatierung für SDK entfernen
        if (this.numericFieldType.indexOf(fieldType) > -1) {
            fieldInfo.format = new FieldInfoFormat();
            // Muss für ImageryLayer komplett entfernt werden
            if(layer.type==="imagery"){
                (fieldInfo as any).format = undefined;
            }
        }
    }
}

export const PopupPanel = withStore(PopupPanelBase);
